import { useHeaderHeight } from "@react-navigation/elements";
import React, { useCallback, useEffect, useState, useRef } from "react";
import { Dimensions, Image, Text, View } from "react-native";
import ImageZoom from "react-native-image-pan-zoom";
import { t } from "../services/i18n";
import commonStyles, {
	isAndroid,
	isNative,
	isWeb,
	SCREEN_HEIGHT,
	SCREEN_WIDTH
} from "../styles/commonStyles";

let _mounted = false;

const ImgZoom = props => {
	const [imageWidth, setImageWidth] = useState(0);
	const [imageHeight, setImageHeight] = useState(0);
	const [isPortrait, setIsPortrait] = useState(true);
	const [imageLoaded, setImageLoaded] = useState(false);
	const [useSimpleImage, setUseSimpleImage] = useState(false);
	const imageSizeAttempted = useRef(false);
	const layoutRef = useRef({ width: 0, height: 0 });

	const headerHeight = useHeaderHeight();

	const { uri } = props.route.params;

	const screenWidth = isWeb ? SCREEN_WIDTH : Dimensions.get("window").width;
	const availableHeight = SCREEN_HEIGHT - headerHeight;

	// Reset state when URI changes
	useEffect(() => {
		setImageWidth(0);
		setImageHeight(0);
		setImageLoaded(false);
		setIsPortrait(true);
		imageSizeAttempted.current = false;
		layoutRef.current = { width: 0, height: 0 };
	}, [uri]);

	// Try to load image size immediately when URI is available
	useEffect(() => {
		if (!uri) {
			console.warn("[ImgZoom] No URI provided");
			return;
		}
		if (imageSizeAttempted.current) return;

		console.log("[ImgZoom] Loading image with URI:", uri.substring(0, 100));

		// Set default dimensions immediately to allow image to render
		const defaultWidth = screenWidth;
		const defaultHeight = availableHeight;
		setImageWidth(defaultWidth);
		setImageHeight(defaultHeight);
		console.log(
			"[ImgZoom] Set default dimensions:",
			defaultWidth,
			defaultHeight
		);

		// Try to get actual size
		imageSizeAttempted.current = true;
		Image.getSize(
			uri,
			(width, height) => {
				if (!_mounted) return;

				console.log("[ImgZoom] Image.getSize success:", width, height);
				const proportion = height / width;
				const isPortraitLayout = availableHeight > screenWidth;

				if (isPortraitLayout) {
					setImageWidth(screenWidth);
					setImageHeight(Math.ceil(screenWidth * proportion));
					setIsPortrait(true);
				} else {
					setImageWidth(Math.ceil(availableHeight / proportion));
					setImageHeight(availableHeight);
					setIsPortrait(false);
				}
			},
			error => {
				// Image.getSize failed - keep default dimensions
				console.warn("[ImgZoom] Image.getSize failed, using defaults:", error);
				// Dimensions already set above, just continue
			}
		);
	}, [uri, screenWidth, availableHeight]);

	const onLayout = useCallback(e => {
		const { height, width } = e.nativeEvent.layout;
		if (height > 0 && width > 0) {
			layoutRef.current = { width, height };
		}
	}, []);

	const onImageLoad = useCallback(
		event => {
			if (!_mounted) return;

			console.log("[ImgZoom] Image loaded event:", event.nativeEvent);

			if (imageLoaded) return;

			const source = event.nativeEvent?.source;
			if (source?.width && source?.height) {
				const actualWidth = source.width;
				const actualHeight = source.height;
				console.log(
					"[ImgZoom] Image actual dimensions:",
					actualWidth,
					actualHeight
				);
				const proportion = actualHeight / actualWidth;
				const isPortraitLayout = availableHeight > screenWidth;

				if (isPortraitLayout) {
					const newHeight = Math.ceil(screenWidth * proportion);
					setImageWidth(screenWidth);
					setImageHeight(newHeight);
					setIsPortrait(true);
				} else {
					const newWidth = Math.ceil(availableHeight / proportion);
					setImageWidth(newWidth);
					setImageHeight(availableHeight);
					setIsPortrait(false);
				}
				setImageLoaded(true);
			} else {
				console.warn(
					"[ImgZoom] Image loaded but no dimensions in source:",
					source
				);
			}
		},
		[screenWidth, availableHeight, imageLoaded]
	);

	const { h100p, bgColor1, color5, font } = commonStyles;

	return (
		<View
			style={[h100p, { height: SCREEN_HEIGHT - headerHeight }, bgColor1]}
			onLayout={onLayout}
		>
			{isPortrait && isNative && (
				<Text
					style={[
						font,
						color5,
						{ fontSize: 18, marginLeft: 10, marginTop: 15 }
					]}
				>
					{t("template:zoom")}
				</Text>
			)}
			{Boolean(imageWidth && imageHeight && uri) &&
				(useSimpleImage || (isAndroid && !imageLoaded) ? (
					<Image
						style={{
							height: availableHeight,
							width: screenWidth,
							flex: 1
						}}
						source={{ uri }}
						onLoad={event => {
							console.log("[ImgZoom] Simple image loaded on Android");
							onImageLoad(event);
							// After image loads, try to use ImageZoom if dimensions are available
							if (isAndroid && imageWidth > 0 && imageHeight > 0) {
								setTimeout(() => {
									if (_mounted) {
										setUseSimpleImage(false);
									}
								}, 100);
							}
						}}
						onError={error => {
							console.error("[ImgZoom] Image load error:", error);
						}}
						resizeMode="contain"
					/>
				) : (
					<ImageZoom
						cropWidth={screenWidth}
						cropHeight={availableHeight}
						imageWidth={imageWidth}
						imageHeight={imageHeight}
						onStartShouldSetPanResponder={() => true}
						minScale={1}
						maxScale={3}
					>
						<Image
							style={{ height: imageHeight, width: imageWidth }}
							source={{ uri }}
							onLoad={onImageLoad}
							onError={error => {
								console.error(
									"[ImgZoom] Image load error in ImageZoom, trying simple image:",
									error
								);
								// Fallback to simple image if ImageZoom fails
								if (isAndroid) {
									setUseSimpleImage(true);
								}
							}}
							resizeMode="contain"
						/>
					</ImageZoom>
				))}
		</View>
	);
};

export default ImgZoom;
